import { Package, TicketPercent } from "lucide-react";
import { formatRp } from "../../lib/api";
import { INTEREST } from "../../lib/ui";

const STAGE_LABEL = {
  introduction: "Perkenalan", qualification: "Kualifikasi", needs_analysis: "Analisis Kebutuhan",
  solution_presentation: "Presentasi Solusi", objection_handling: "Penanganan Keberatan",
  close: "Closing", end_conversation: "Selesai",
};

function ScoreBar({ label, value, color, reason }) {
  return (
    <div title={reason}>
      <div className="flex justify-between text-xs">
        <span className="text-muted">{label}</span>
        <span className="font-mono">{value}</span>
      </div>
      <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-white/5">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${value}%` }} />
      </div>
    </div>
  );
}

export function ProfilePanel({ chat }) {
  const cfg = INTEREST[chat.interest_level];
  const score = chat.score;
  return (
    <aside className="hidden h-screen w-[360px] shrink-0 overflow-y-auto border-l border-white/5 bg-panel p-5 xl:block" data-testid="profile-panel">
      <div className="rounded-2xl border border-white/5 bg-panel2 p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-faint">Status Lead</p>
            <p className={`mt-1 text-lg font-semibold ${cfg ? "" : "text-faint"}`}>
              {cfg ? cfg.label : "Belum Diketahui"}
            </p>
            <p className="mt-0.5 text-xs text-muted">{cfg ? cfg.desc : "Menunggu sinyal percakapan"}</p>
          </div>
          <div className={`flex h-14 w-14 items-center justify-center rounded-full border-2 font-mono text-sm font-bold ${
            cfg ? "border-mint text-mint" : "border-white/10 text-faint"}`} title="Probabilitas closing">
            {chat.close_probability || 0}%
          </div>
        </div>
        <p className="mt-3 inline-block rounded-full bg-white/5 px-2.5 py-1 text-[11px] text-muted">
          Stage: {STAGE_LABEL[chat.stage] || chat.stage || "-"}
        </p>
      </div>

      <section className="mt-5">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-faint">Produk Dipilih</h3>
        <div className="mt-2 space-y-2">
          {(chat.selected_products || []).map((p) => (
            <div key={p.code} className="flex items-center gap-3 rounded-xl border border-white/5 bg-panel2 p-3">
              {p.image_url ? (
                <img src={p.image_url} alt={p.name} className="h-9 w-9 rounded-lg object-cover" />
              ) : (
                <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-panel3"><Package size={14} className="text-mint" /></span>
              )}
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{p.name}</p>
                <p className="font-mono text-[11px] text-faint">{p.qty} × {formatRp(p.price)}</p>
              </div>
              <span className="font-mono text-xs text-mint">{formatRp(p.qty * p.price)}</span>
            </div>
          ))}
          {!chat.selected_products?.length && <p className="text-xs text-faint">Belum ada produk yang dibahas.</p>}
        </div>
      </section>

      <section className="mt-5 rounded-2xl border border-mint/15 bg-mint/5 p-4">
        <p className="text-xs text-muted">Estimasi Revenue</p>
        <p data-testid="estimated-revenue" className="mt-1 font-mono text-2xl font-semibold text-mint">{formatRp(chat.estimated_revenue)}</p>
        <p className="mt-1 font-mono text-[11px] text-faint">Probabilitas closing: {chat.close_probability || 0}%</p>
      </section>

      {chat.recent_context && (
        <section className="mt-5">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-faint">Konteks Terkini</h3>
          <p className="mt-2 rounded-xl border border-white/5 bg-panel2 p-3 text-xs leading-relaxed text-muted" data-testid="recent-context">
            {chat.recent_context}
          </p>
        </section>
      )}

      {score && (
        <section className="mt-5">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-faint">Skor Percakapan</h3>
          <div className="mt-3 space-y-4 rounded-xl border border-white/5 bg-panel2 p-4" data-testid="conversation-scores">
            <ScoreBar label="Opening" value={score.opening?.value || 0} color="bg-mint" reason={score.opening?.reason} />
            <ScoreBar label="Engagement" value={score.engagement?.value || 0} color="bg-amber" reason={score.engagement?.reason} />
            <ScoreBar label="Closing" value={score.closing?.value || 0} color="bg-slate" reason={score.closing?.reason} />
            {score.closing?.improvement && (
              <p className="border-t border-white/5 pt-3 text-[11px] leading-relaxed text-faint">
                Saran: {score.closing.improvement}
              </p>
            )}
          </div>
        </section>
      )}
    </aside>
  );
}
